import React from 'react'
import styled from 'styled-components'
import CameraIcon from '../../icons/IconLib/CameraIcon'
import { CameraPreviewStatus } from './useCameraPreview'

export interface PhotoCaptureTileProps {
  videoRef: React.RefObject<HTMLVideoElement | null>
  status: CameraPreviewStatus
  disabled: boolean
  onClick: () => void
}

/** Camera tile: live viewfinder when the stream is up, neutral fallback otherwise. */
const PhotoCaptureTile: React.FC<PhotoCaptureTileProps> = ({
  videoRef,
  status,
  disabled,
  onClick,
}) => (
  <Tile type='button' onClick={onClick} disabled={disabled} aria-label='צילום' title='צילום'>
    {/*
      Kept mounted across statuses so the ref exists by the time the stream
      arrives. muted + playsInline are what stop iOS from going fullscreen.
    */}
    <Video ref={videoRef} $visible={status === 'streaming'} muted playsInline autoPlay />
    {status !== 'streaming' && (
      <Fallback>
        <CameraIcon size={28} />
      </Fallback>
    )}
  </Tile>
)

export default PhotoCaptureTile

const Tile = styled.button`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  background: ${({ theme }) => theme.colors.bgLayout};
  color: ${({ theme }) => theme.colors.icon};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

const Video = styled.video<{ $visible: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`

const Fallback = styled.span`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.ink4};
`
